import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { environment } from '@environments/environment';
import { catchError, Observable, retry } from 'rxjs';
import { SessionStorageService } from './storage.service';
import { UserService } from './user.service';

export interface IRackingRuleSortItem {
	priority: number;
	order: 0 | 1;
	type: {
		self:
			| '/racking/configuration-sorting-strategies/profileLength'
			| '/racking/configuration-sorting-strategies/productionSequenceNumber'
			| '/racking/configuration-sorting-strategies/modelIndex'
			| '/racking/configuration-sorting-strategies/sequenceNumber'
			| '/racking/configuration-sorting-strategies/component'
			| '/racking/configuration-sorting-strategies/operation';
	};
}

export interface IRackingConfigs {
	self: string;
	emptySpacesBetweenRules: number | null;
	components: string[];
}

export interface IRackingConfigsResponse {
	items: [IRackingConfigs];
}

// Ignore is UI-only: the API enum is Include|Exclude, and an ignored filter is not sent.
export enum WorkCenterFilterMode {
	Ignore = 'Ignore',
	Include = 'Include',
	Exclude = 'Exclude',
}

export interface IRack {
	self: string;
	name: string;
	description: string;
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
	rows: number;
	columns: number;
	components: string[];
	translationComponents: string;
	width: number;
	height: number;
	depth: number;
	capacity: number;
	workCenterFilterMode: WorkCenterFilterMode;
	workCenterFilterKeys: string[];
}

export interface IRacks {
	self: string;
	items: IResponseRack[];
}

export interface IBaseRack {
	self: string;
	name: string;
	description: string;
	configuration: {
		cells: number;
		rows: number;
		columns: number;
		cell: {
			width: number;
			height: number;
			depth: number;
		};
		gap: boolean;
	};
	components: {
		type: {
			self: string;
		};
	}[];
	capacity: {
		maximum: number;
	};
	workCenters?: {
		mode: WorkCenterFilterMode.Include | WorkCenterFilterMode.Exclude;
		keys: string[];
	} | null;
}

export interface IRequestRack extends IBaseRack {
	// No additional properties
}

export interface IMeta {
	meta: {
		createdAt: number;
		createdBy: {
			self: string;
		};
		updatedAt: number;
		updatedBy: {
			self: string;
		};
		version: number;
	};
}

export interface ITag {
	name: string;
	value: string;
}

export interface IResponseRack extends IBaseRack, IMeta {
	tags: ITag[];
}

export interface IRackReference {
	rack: {
		self: string;
	};
	priority?: number;
}

export interface IRackset {
	self: string;
	name: string;
	description: string;
	racks: MatTableDataSource<IRackReference>;
	selectedRacks: string[];
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
}

export interface IRackDetails {
	name: string;
	description: string;
	cells: string;
	rows: string;
	columns: string;
	components: string;
	updatedDate: string;
}

export interface IBaseRackset {
	self: string;
	name: string;
	description: string;
	racks: IRackReference[];
}

export interface IResponseRackset extends IBaseRackset, IMeta {
	tags: ITag[];
}

export interface IRequestRackset extends IBaseRackset {
	// No additional properties
}

export interface IRacksets {
	self: string;
	items: IResponseRackset[];
}

export interface IRule {
	self: string;
	name: string;
	description: string;
	batchType?: string;
	useCoatingInOptimization: boolean;
	material: string;
	machine: string;
	profile: string;
	groupByProductionRoute: boolean;
	slotsPerCell: number;
	slotConfiguration: 'MixElements' | 'MixCompleteElements' | 'SeparateElements' | 'GroupSimilarElements';
	productionSequenceNumberMode: 'Disabled' | 'Slave' | 'Master';
	rackset: string;
	placementPriority: number | null;
	segregation: string;
	optimization: string;
	sorting: IRackingRuleSortItem[];
	updatedDate: string;
	updatedAtEpoch?: number; // kept beside updatedDate by the lists, which sort by it; a new or imported item has none yet
	updatedBy: string;
	id0: string;
	id1: string;
	id2: string;
	id3: string;
	id4: string;
	id5: string;
	id6: string;
	id7: string;
	id8: string;
	id9: string;
	state: 'Any' | 'NoGeometry' | 'OnlyGeometry';
	logisticsFormula: string | null;
}

export interface IRules {
	self: string;
	items: IResponseRule[];
}

export interface IBaseRule {
	self: string;
	name: string;
	description: string | null;
	batchType?: {
		self: string;
	};
	useCoatingInOptimization: boolean;
	profile: {
		self: string;
	} | null;
	groupByProductionRoute: boolean;
	slotConfiguration: 'MixElements' | 'MixCompleteElements' | 'SeparateElements' | 'GroupSimilarElements';
	slotsPerCell: number;
	productionSequenceNumberMode: 'Disabled' | 'Slave' | 'Master';
	rackset: {
		self: string;
	} | null;
	default: {
		identifiers: [string, string, string, string, string, string, string, string, string, string];
		state: 'Any' | 'NoGeometry' | 'OnlyGeometry';
	} | null;
	aluminium: {
		material: {
			self: string;
		};
		machine: {
			self: string;
		};
	};
	placementPriority: number | null;
	segregation: {
		identifier: string;
	};
	optimization: {
		identifier: string;
	};
	logisticsFormula?: string | null;
	sorting: IRackingRuleSortItem[];
}

export interface IResponseRule extends IBaseRule, IMeta {
	tags: ITag[];
}

export interface IRequestRule extends IBaseRule {
	// No additional properties
}

@Injectable({
	providedIn: 'root',
})
export class RackingService {
	public constructor(
		private httpClient: HttpClient,
		private userService: UserService,
		private sessionStorageService: SessionStorageService,
	) {}

	public getConfigs(): Observable<IRackingConfigsResponse> {
		const URL = `${environment.awsEnvUri}/racking/configs`;
		return this.httpClient
			.get<IRackingConfigsResponse>(URL, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getRacks(): Observable<IRacks> {
		const URL = `${environment.awsEnvUri}/racking/racks`;
		return this.httpClient
			.get<IRacks>(URL, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public getRules(): Observable<IRules> {
		const apiUrl = `${environment.awsEnvUri}/racking/rules`;
		return this.httpClient
			.get<IRules>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createRule(rule: IRule): Observable<IRequestRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = `${environment.awsEnvUri}/racking/rules`;
		return this.httpClient
			.post<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateRule(rule: IRule): Observable<IResponseRule> {
		const body: IRequestRule = this.mapRuleToRequestRule(rule);
		const apiUrl = environment.awsEnvUri + rule.self;
		return this.httpClient
			.put<IRequestRule>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateRack(rack: IRack): Observable<IResponseRack> {
		const body: IRequestRack = toRequestRack(rack);
		const apiUrl = environment.awsEnvUri + rack.self;
		return this.httpClient
			.put<IRequestRack>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public createRack(rack: IRack): Observable<IRequestRack> {
		const body: IRequestRack = toRequestRack(rack);
		const apiUrl = `${environment.awsEnvUri}/racking/racks`;
		return this.httpClient
			.post<IRequestRack>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public delete(reference: string): Observable<any> {
		const apiUrl = environment.awsEnvUri + reference;
		return this.httpClient
			.delete(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateConfig(config: IRackingConfigs): Observable<IRackingConfigs> {
		const SLASH = config.self.lastIndexOf('/');
		const UUID = config.self.substring(SLASH + 1);
		const body: IRackingConfigs = {
			self: config.self,
			emptySpacesBetweenRules: config.emptySpacesBetweenRules,
			components: config.components,
		};
		const URL = `${environment.awsEnvUri}/racking/configs/${UUID}`;
		return this.httpClient
			.put<IRackingConfigs>(URL, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private getHeaders() {
		const accountId: string = this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	private handleError(error: HttpErrorResponse) {
		throw error;
		return [];
	}

	public getLastSegment(urlOrObject: string | any, keyPath?: string[]) {
		let url: string;

		if (typeof urlOrObject === 'string') {
			url = urlOrObject;
		} else if (Array.isArray(keyPath)) {
			url = keyPath.reduce((acc, key) => acc[key], urlOrObject);
		} else {
			throw new TypeError('Invalid arguments');
		}

		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	public getRacksets(): Observable<IRacksets> {
		const apiUrl = `${environment.awsEnvUri}/racking/racksets`;
		return this.httpClient
			.get<IRacksets>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createRackset(rackset: IRackset): Observable<IRequestRackset> {
		const body: IRequestRackset = toRequestRackset(rackset);
		const apiUrl = `${environment.awsEnvUri}/racking/racksets`;
		return this.httpClient
			.post<IRequestRackset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public updateRackset(rackset: IRackset): Observable<IResponseRackset> {
		const body: IRequestRackset = toRequestRackset(rackset);
		const apiUrl = environment.awsEnvUri + rackset.self;
		return this.httpClient
			.put<IRequestRackset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	removeRackFromRackset(rackset: IRackset): Observable<IResponseRackset> {
		const body: IRequestRackset = {
			self: rackset.self,
			name: rackset.name,
			description: rackset.description,
			racks: rackset.racks.data.map((ref) => ({ rack: { self: ref.rack.self } })),
		};
		const apiUrl = environment.awsEnvUri + rackset.self;
		return this.httpClient
			.put<IRequestRackset>(apiUrl, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private getEngine() {
		return this.userService.account.defaultEngine;
	}

	mapRuleToRequestRule(rule: IRule): IRequestRule {
		const defaultEngine = this.getEngine();
		const body: IRequestRule = {
			self: rule.self,
			name: rule.name,
			description: rule.description,
			useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
			groupByProductionRoute: rule.groupByProductionRoute,
			slotsPerCell: rule.slotsPerCell,
			slotConfiguration: rule.slotConfiguration,
			productionSequenceNumberMode: rule.productionSequenceNumberMode,
			...(rule.batchType && {
				batchType: {
					self: `/mdm/apps/one/stores/${defaultEngine}/production-batch-types/${rule.batchType}`,
				},
			}),
			...(rule.material &&
				rule.machine && {
					aluminium: {
						material: {
							self: `/mdm/apps/one/stores/${defaultEngine}/component-inventory-items/${rule.material}`,
						},
						machine: {
							self: `/mdm/apps/one/stores/${defaultEngine}/production-machines/${rule.machine}`,
						},
					},
				}),
			...(rule.profile && {
				profile: {
					self: `/mdm/apps/one/stores/${defaultEngine}/component-profiles/${rule.profile}`,
				},
			}),
			...(rule.state && {
				default: {
					identifiers: [rule.id0, rule.id1, rule.id2, rule.id3, rule.id4, rule.id5, rule.id6, rule.id7, rule.id8, rule.id9],
					state: rule.state,
				},
			}),
			...(rule.rackset && {
				rackset: {
					self: rule.rackset,
				},
			}),
			placementPriority: rule.placementPriority,
			...(rule.segregation && {
				segregation: {
					identifier: rule.segregation,
				},
			}),
			...(rule.optimization && {
				// This property is not implemented
				optimization: {
					identifier: rule.optimization,
				},
			}),
			...(rule.logisticsFormula?.trim() && {
				logisticsFormula: rule.logisticsFormula,
			}),
			sorting: rule.sorting,
		};
		return body;
	}
}

function toRequestRack(rack: IRack): IRequestRack {
	const body: IRequestRack = {
		self: rack.self,
		name: rack.name,
		description: rack.description,
		configuration: {
			cells: rack.rows * rack.columns,
			rows: rack.rows,
			columns: rack.columns,
			cell: {
				width: rack.width,
				height: rack.height,
				depth: rack.depth,
			},
			gap: false,
		},
		components: rack.components.map((component) => ({
			type: { self: component },
		})),
		capacity: {
			maximum: rack.capacity,
		},
		// The API refuses an empty key list and asks for the filter to be omitted instead, so a
		// mode without keys is sent the same way Ignore is: as no filter at all.
		...(rack.workCenterFilterMode !== WorkCenterFilterMode.Ignore &&
			(rack.workCenterFilterKeys?.length ?? 0) > 0 && {
				workCenters: {
					mode: rack.workCenterFilterMode,
					keys: rack.workCenterFilterKeys,
				},
			}),
	};

	return body;
}

function toRequestRackset(rackset: IRackset): IRequestRackset {
	const racksData = Array.isArray(rackset.racks) ? rackset.racks : Array.isArray(rackset.racks?.data) ? rackset.racks.data : [];

	return {
		self: rackset.self,
		name: rackset.name,
		description: rackset.description,
		racks: racksData.map((ref, index) => ({
			rack: { self: ref.rack?.self ?? ref },
			priority: ref.priority ?? index + 1,
		})),
	};
}
